import { type AvatarSize } from '@craft/ui/components/avatars';
import { getGravatarSize } from '@craft/ui/components/users';

import { useUserAvatarQuery } from '@/src/generated/operations/UserAvatar.generated';
import { createQueryHook } from '@/src/integrations/urql/queries';

const useRootUserAvatar = createQueryHook(useUserAvatarQuery, {
  context: { additionalTypenames: ['Avatar'] },
  errorMessage: 'There was a problem loading avatar data.',
  responseTransformer: data => data.avatar,
});

/**
 * Fetches the {@link Avatar} for the user with the given id, sized for the avatar being rendered.
 *
 * The blur placeholder must be requested explicitly via the 'includeBlur' option: it carries a
 * per-user upstream fetch and payload cost, so callers opt in only where the progressive load is
 * actually perceptible (large, single-user renders), never for small or manually-sized avatars.
 */
export const useUserAvatar = (
  userId: string,
  size: AvatarSize,
  options: { readonly includeBlur: boolean; readonly pause?: boolean },
) =>
  useRootUserAvatar(
    { includeBlur: options.includeBlur, size: getGravatarSize(size), userId },
    { pause: options.pause },
  );
